import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  PlateMapAnnotationError,
  PlateMapErrorLevel,
  PlateMapErrorType,
  ValidationErrorsByLevel,
} from 'samples/plate-maps/validation/ValidationTypes';
import {
  InlineBannerActionProps,
  InlineBannerActionType,
  InlineBannerProps,
  InlineBannerType,
} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';

const ERROR_LEVEL_TO_INLINE_BANNER_TYPE: {[key: string]: InlineBannerType} = {
  [PlateMapErrorLevel.ERROR]: InlineBannerType.ERROR,
  [PlateMapErrorLevel.WARNING]: InlineBannerType.WARNING,
  [PlateMapErrorLevel.INFO]: InlineBannerType.INFO,
};

const ERROR_LEVEL_TO_SUMMARY_MESSAGE: {[key: string]: string} = {
  [PlateMapErrorLevel.ERROR]: 'Multiple values are invalid.',
  [PlateMapErrorLevel.WARNING]: 'Multiple values have been updated.',
  [PlateMapErrorLevel.INFO]: 'Multiple values have additional information.',
};

const ERROR_LEVEL_TO_ERROR_NAME: {[key: string]: string} = {
  [PlateMapErrorLevel.ERROR]: 'errors',
  [PlateMapErrorLevel.WARNING]: 'warnings',
  [PlateMapErrorLevel.INFO]: 'messages',
};

interface PlateMapValidationBannerProps {
  errorsByLevel: ValidationErrorsByLevel;
  onResolveError: (errors: Array<PlateMapAnnotationError>) => void;
  onDismissErrors: (errorUuids: Set<string>) => void;
}

/**
 * A stack of banners for plate map validation errors, where errors are summarized in one banner per error level.
 */
export default function PlateMapValidationBanner({
  errorsByLevel,
  onResolveError,
  onDismissErrors,
}: PlateMapValidationBannerProps): JSX.Element {
  const inlineBannerStackMessages: Array<InlineBannerProps> = [];

  for (const level of [PlateMapErrorLevel.ERROR, PlateMapErrorLevel.WARNING, PlateMapErrorLevel.INFO]) {
    const errors = errorsByLevel[level] ?? [];
    const dedupedMessages = [...new Set(errors.map(({error: {message}}) => message))];
    if (errors.length === 0) {
      continue;
    }
    const hasLayerError = errors.some(({error: {type}}) => type === PlateMapErrorType.LAYER);
    const sharedActionProps = {
      accordionLabels: {
        moreLabel: `Show ${ERROR_LEVEL_TO_ERROR_NAME[level]}`,
        lessLabel: `Hide ${ERROR_LEVEL_TO_ERROR_NAME[level]}`,
      },
      rightAlignAccordionButton: true,
    };
    let actionProps: InlineBannerActionProps;
    if (level === PlateMapErrorLevel.ERROR) {
      actionProps = {
        ...sharedActionProps,
        actionType: InlineBannerActionType.ACCORDION_AND_CUSTOM,
        actionText: hasLayerError ? 'Remove layer' : 'clear invalid values.',
        actionOnClick: () => {
          onResolveError(errors.map((error) => error.error));
        },
        rightAlignActionButton: false,
      };
    } else {
      actionProps = {...sharedActionProps, actionType: InlineBannerActionType.ACCORDION};
    }
    const primaryMessage =
      dedupedMessages.length === 1 ? dedupedMessages[0] : ERROR_LEVEL_TO_SUMMARY_MESSAGE[level];
    inlineBannerStackMessages.push({
      bannerType: ERROR_LEVEL_TO_INLINE_BANNER_TYPE[level],
      text:
        level === PlateMapErrorLevel.ERROR && !hasLayerError
          ? `${primaryMessage} Reassign or`
          : primaryMessage,
      supportingInfo:
        dedupedMessages.length > 1 ? (
          <div>
            {dedupedMessages.map((message) => (
              <span className={css(styles.bannerSubtext)} key={message}>
                {message}
              </span>
            ))}
          </div>
        ) : undefined,
      action: actionProps,
      ...(level === PlateMapErrorLevel.WARNING
        ? {
            onDismiss: () => {
              onDismissErrors(new Set(errors.map((error) => error.uuid)));
            },
          }
        : {}),
    });
  }
  return <InlineBannerStack messages={inlineBannerStackMessages} />;
}

const styles = StyleSheet.create({
  bannerSubtext: {
    display: 'block',
  },
});
